import { Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SegmentValue } from '@ionic/angular';
import { map, switchMap, take } from 'rxjs/operators';
import { IOption } from 'src/app/components/shared/form/components/form-select/form-select.model';
import { BUTTON_CLICK } from 'src/app/components/shared/modal/exit-modal/exit-modal.constants';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import {
  Country,
  LocationProfile,
} from 'src/app/core/models/admin/location-admin.model';
import { UserAdmin } from 'src/app/core/models/admin/user-admin.model';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { UserService } from 'src/app/core/services/admin/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { LocationFormUtil } from 'src/app/core/utils/location-form/location-form.utils';
import { LocationPermissionUtils } from 'src/app/core/utils/permissions/location-permissions/location-permissions.utils';
import {
  BUTTON_TEXT,
  CANCEL_MODAL,
  CONSTS,
  TABS,
} from './location-profile-popup.constants';
import { ProfileBundle } from './location-profile.model';

/** LocationProfilePopupComponent
 * This component is responsible for creating and editing a Location Profile.
 * It is rendered inside a Material Dialog and uses a multi-tab workflow:
 *  - Location
 *  - Group
 *  - Configuration
 *
 * The component manages:
 *  - Reactive form initialization and validation
 *  - Tab navigation and section-level validation
 *  - Role-based access control
 *  - Country master data loading
 *  - Create / Update submission logic
 *  - Unsaved changes warning
 */
@Component({
  selector: 'app-location-profile-popup',
  templateUrl: './location-profile-popup.component.html',
  styleUrls: ['./location-profile-popup.component.scss'],
  standalone: false,
})
export class LocationProfilePopupComponent implements OnInit {
  private userService = inject(UserService);
  readonly destroy = inject(DestroyRef);

  currentTab: string | SegmentValue = TABS.LOCATION;
  nextTab: string | SegmentValue = '';
  currentUser!: UserAdmin;

  form!: FormGroup;
  initialFormData!: LocationProfile;
  fullCountryList!: Country[];
  countryOptionList!: IOption[];

  readonly AVAILABLE_TABS = TABS;
  readonly CONSTANTS = CONSTS;
  readonly CANCEL_MODAL = CANCEL_MODAL;
  readonly BUTTON_TEXT = BUTTON_TEXT;

  currentLocation: any;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ProfileBundle,
    public readonly dialogRef: MatDialogRef<LocationProfilePopupComponent>,

    private locationService: LocationService,
    private profileUtil: LocationFormUtil,
    private roleUtils: LocationPermissionUtils,
    private commonUtils: CommonUtils,
  ) {}

  /**
   * Angular lifecycle hook
   *
   * - Initializes the reactive form
   * - Stores initial form state
   * - Fetches current user
   * - Loads country master data
   * - Syncs country and timezone from current location if missing
   */
  ngOnInit(): void {
    this.form = this.profileUtil.initForm(this.data?.profile);
    this.initialFormData = { ...this.form.getRawValue() };
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        switchMap((user) => {
          this.currentUser = user;
          return this.getAllCountries();
        }),
      )
      .subscribe();

    this.locationService
      .getCurrentLocation$()
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((loc) => {
        this.currentLocation = loc;
        if (!this.form.get('locationDetails')?.get('country')?.value) {
          this.form
            .get('locationDetails')
            ?.get('country')
            ?.setValue(
              this.currentLocation.location.locationDetails.country.isoCode,
            );
        }
        if (!this.form.get('locationDetails')?.get('timeZone')?.value) {
          this.form
            .get('locationDetails')
            ?.get('timeZone')
            ?.setValue(this.currentLocation.location.locationDetails.timeZone);
        }
      });
  }

  /**
   * Handles tab change event
   * @param $event selected tab value
   */
  onTabSelected($event: string): void {
    this.currentTab = $event;
  }

  /**
   * Handles dialog close action
   * - Shows warning modal if form is dirty
   * - Closes dialog directly if no changes
   */
  onClose() {
    if (this.form.dirty) {
      this.openWarningModal();
    } else {
      this.dialogRef.close();
    }
  }

  /**
   * Handles right button click.
   *
   * Behavior depends on mode:
   * --------------------------
   * - Edit mode OR last tab → Save
   * - Otherwise             → Go to next tab
   */
  rightButtonClick() {
    this.data.edit || this.currentTab === TABS.CONFIGURATION
      ? this.onSubmit()
      : this.onNext();
  }

  /**
   * Handles left button click (Reset).
   * Only resets the currently active tab
   * without affecting other sections.
   */
  leftButtonClick() {
    switch (this.currentTab) {
      case TABS.LOCATION:
        this.form
          .get('locationDetails')
          ?.patchValue(this.initialFormData.locationDetails);
        this.form.get('locationDetails')?.markAsPristine();
        this.form.get('locationDetails')?.markAsUntouched();
        break;
      case TABS.GROUP:
        this.form
          .get('locationGroups')
          ?.patchValue(this.initialFormData.locationGroups);
        this.form.get('locationGroups')?.markAsPristine();
        this.form.get('locationGroups')?.markAsUntouched();
        break;
      case TABS.CONFIGURATION:
        this.form
          .get('configurations')
          ?.patchValue(this.initialFormData.configurations);
        this.form.get('configurations')?.markAsPristine();
        this.form.get('configurations')?.markAsUntouched();
        break;
    }
  }

  /** Returns location details form group */
  get locationForm() {
    return this.form.get('locationDetails') as FormGroup;
  }

  /** Returns configuration form group */
  get configForm() {
    return this.form.get('configurations') as FormGroup;
  }

  /** Configuration for left (Reset) button */
  get getLeftButton() {
    return {
      text: BUTTON_TEXT.RESET,
      imageSrc: '',
      type: BUTTON_TYPES.CANCEL_BUTTON,
      disable: false,
    };
  }

  /** Checks whether entire form is valid */
  isEntireFormValid(): boolean {
    return this.form.valid;
  }

  /**
   * Configuration for right (Save / Next) button
   * Includes validation and disabled logic
   */
  get getRightButton() {
    return this.data.edit || this.currentTab === TABS.CONFIGURATION
      ? {
          text: BUTTON_TEXT.SAVE,
          imageSrc: '',
          type: BUTTON_TYPES.TEXT_BUTTON,
          disable:
            !this.isEntireFormValid() ||
            (!this.isEntireFormValid() &&
              !this.getIsSectionValid(this.currentTab)),
        }
      : {
          text: BUTTON_TEXT.NEXT,
          imageSrc: '',
          type: BUTTON_TYPES.TEXT_BUTTON,
          disable: !this.getIsSectionValid(this.currentTab),
        };
  }

  /**
   * Validates a specific tab section.
   *
   * A section is considered valid ONLY if:
   *  - It is valid
   *  - It has been modified (dirty)
   */
  getIsSectionValid(tab: SegmentValue): boolean {
    switch (tab) {
      case TABS.LOCATION:
        return (
          (this.form?.get('locationDetails')?.valid &&
            this.form?.get('locationDetails')?.dirty) ||
          false
        );
      case TABS.GROUP:
        return (
          (this.form?.get('locationGroups')?.get('assignedGroups')?.valid &&
            this.form?.get('locationGroups')?.get('assignedGroups')?.dirty) ||
          false
        );
      case TABS.CONFIGURATION:
        return (
          (this.form?.get('configurations')?.valid &&
            this.form?.get('configurations')?.dirty) ||
          false
        );
      default:
        return false;
    }
  }

  /**
   * Returns unit based on selected country
   * Defaults to 'M' if not found
   */
  get getCurrUnit() {
    return (
      this.fullCountryList?.find(
        (val) =>
          val.isoCode ===
          this.form.get('locationDetails')?.get('country')?.value,
      )?.unit ?? 'M'
    );
  }

  /**
   * Moves user to the next tab in sequence.
   */
  private onNext() {
    if (this.currentTab === TABS.LOCATION) {
      this.onTabSelected(TABS.GROUP);
    } else if (this.currentTab === TABS.GROUP) {
      this.onTabSelected(TABS.CONFIGURATION);
    }
  }

  /**
   * Handles final form submission.
   *
   * - Calls ADD API in create mode
   * - Calls UPDATE API in edit mode
   */
  private onSubmit(): void {
    const obs$ = this.data.edit
      ? this.locationService.updateLocation(
          this.getSubmitForm(),
          this.form.get('locationDetails')?.get('code')?.value,
        )
      : this.locationService.addLocation(this.getSubmitForm());
    obs$.pipe(take(1)).subscribe((response) => {
      this.dialogRef.close(response);
    });
  }

  /**
   * Opens a confirmation modal when user
   * attempts to close dialog with unsaved changes.
   */
  private openWarningModal() {
    this.commonUtils
      .openWarningModal({
        header: CANCEL_MODAL.HEADER,
        body: CANCEL_MODAL.BODY,
        confirmButtonText: CANCEL_MODAL.CONFIRM_BUTTON,
        rejectButtonText: CANCEL_MODAL.REJECT_BUTTON,
      })
      .afterClosed()
      .subscribe((resp) => {
        if (resp.button === BUTTON_CLICK.ACCEPT) {
          this.dialogRef.close();
        }
      });
  }

  /**
   * Builds backend-ready request payload.
   *
   * Responsibilities:
   * -----------------
   * - Replace country ISO code with full country object
   * - Inject derived values required by backend
   * - Preserve deeply nested configuration structure
   */
  private getSubmitForm() {
    const rawForm: LocationProfile = this.form.getRawValue();
    const fullCountry = this.fullCountryList.find(
      (val) =>
        val.isoCode === this.form.get('locationDetails')?.get('country')?.value,
    ) as Country;
    return {
      locationDetails: {
        ...rawForm.locationDetails,
        country: fullCountry,
      },
      locationGroups: this.form
        .get('locationGroups')
        ?.get('assignedGroups')
        ?.getRawValue(),
      configurations: {
        ...rawForm.configurations,
        gslConfigurations: {
          ...rawForm.configurations.gslConfigurations,
          revenueRecoveryDetails: {
            revRecoveryPackage: {
              ...rawForm.configurations.gslConfigurations.revenueRecoveryDetails
                .revRecoveryPackage,
              origSlic: rawForm.locationDetails.slic as string,
              origCountry: this.form.get('locationDetails')?.get('country')
                ?.value as string,
              locCode: rawForm.locationDetails.code,
            },
            revRecoveryPallet: {
              ...rawForm.configurations.gslConfigurations.revenueRecoveryDetails
                .revRecoveryPallet,
              origSlic: rawForm.locationDetails.slic as string,
              origCountry: this.form.get('locationDetails')?.get('country')
                ?.value as string,
              locCode: rawForm.locationDetails.code,
            },
          },
        },
      },
    };
  }

  /**
   * Fetches country master data from backend.
   *
   * - Sorts countries alphabetically by ISO code
   * - Builds dropdown-friendly list
   * - Stores full list for payload mapping
   */
  private getAllCountries() {
    return this.locationService.getAllCountries().pipe(
      takeUntilDestroyed(this.destroy),
      map((data) => {
        if (data) {
          const returnData = data.response.country;
          returnData.sort((a, b) =>
            a.isoCode.toLowerCase().localeCompare(b.isoCode.toLowerCase()),
          );
          this.countryOptionList = returnData.map((countries: Country) => {
            return {
              label: countries.isoCode,
              value: countries.isoCode,
            };
          });
          this.fullCountryList = data.response.country;
        }
      }),
    );
  }
}
